import { formatDistanceToNow, format as dateFnsFormat, isValid } from 'date-fns'

export const formatDate = (date, format = 'MMM d, yyyy') => {
  if (!date) return ''
  const dateObj = new Date(date)
  if (!isValid(dateObj)) return ''
  return dateFnsFormat(dateObj, format)
}

export const formatDateTime = (date) => {
  return formatDate(date, 'MMM d, yyyy h:mm a')
}

// SQLite CURRENT_TIMESTAMP is UTC in "YYYY-MM-DD HH:MM:SS" form (no timezone).
// Browsers parse that space-separated string as LOCAL time, which is wrong —
// normalise it to UTC so it converts to the viewer's local clock correctly.
const parseServerDate = (date) => {
  if (!date) return null
  if (date instanceof Date) return date
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
    return new Date(date.replace(' ', 'T') + 'Z')
  }
  return new Date(date)
}

export const formatRelativeTime = (date) => {
  const dateObj = parseServerDate(date)
  if (!dateObj || !isValid(dateObj)) return ''
  return formatDistanceToNow(dateObj, { addSuffix: true })
}

// Local clock time, e.g. "20:31" — adapts to the viewer's system timezone.
export const formatClockTime = (date) => {
  const dateObj = parseServerDate(date)
  if (!dateObj || !isValid(dateObj)) return ''
  return dateFnsFormat(dateObj, 'HH:mm')
}

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

export const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US').format(number)
}

export const formatPercentage = (value, decimals = 1) => {
  return `${(value * 100).toFixed(decimals)}%`
}

export const truncateText = (text, maxLength = 100, suffix = '...') => {
  if (!text || text.length <= maxLength) return text || ''
  return text.substring(0, maxLength) + suffix
}

export const formatSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // Remove invalid chars
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return ''
  
  // Remove all non-digits
  const cleaned = phoneNumber.replace(/\D/g, '')
  
  // Check if the input is of correct length
  if (cleaned.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
  }
  
  return phoneNumber
}

export const capitalizeFirst = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const capitalizeWords = (str) => {
  if (!str) return ''
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

export const formatInitials = (name) => {
  if (!name) return ''
  
  const parts = name.trim().split(' ')
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export const stripHtml = (html) => {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

export const getReadingTime = (text, wordsPerMinute = 200) => {
  const words = text.trim().split(/\s+/).length
  const time = Math.ceil(words / wordsPerMinute)
  return `${time} min read`
}

export const formatColorToHex = (color) => {
  // If already hex, return as is
  if (color.startsWith('#')) return color
  
  // Convert RGB to hex
  if (color.startsWith('rgb')) {
    const values = color.match(/\d+/g)
    if (values && values.length >= 3) {
      const hex = values.slice(0, 3)
        .map(val => parseInt(val).toString(16).padStart(2, '0'))
        .join('')
      return `#${hex}`
    }
  }
  
  return color
}